import { fromJS } from 'immutable';
import { recordEvent } from '@pearsontechnology/bronte-lib-fullstory';
import { logNewrelicAddPageAction } from '../../cookies/NewRelic';
import CoursewareItemProvider from './CoursewareItemProvider';

class APICoursewareItemService {
  constructor({ id, target, context/* , coursewareType */ }) {
    this.target = target || { id };
    this.context = context;
    this._subscriptions = [];
  }

  async evaluate(elementId) {
    if (this.context === 'HISTORY') {
      const { piSession } = window;
      const piUserId = piSession.userId();
      const eventName = 'evaluationSkipped';
      const screenTitle = await CoursewareItemProvider.getInCoursewareState(elementId, ['title']);
      const eventData = { coursewareId: elementId, type: 'contextHistory', piUserId, description: 'evaluation skipped due to history context', name: screenTitle };
      logNewrelicAddPageAction(eventName, eventData);
      recordEvent(eventName, eventData);
      return undefined;
    }
    const evaluationResults = await CoursewareItemProvider.evaluateDeployment(elementId || this.target.id);
    // notify to fetch the feedback result
    this.notifySubscribers(elementId);
    return evaluationResults;
  }

  restart(activityId) {
    return CoursewareItemProvider.restartActivity(activityId);
  }

  async getResult(elementId) {
    const evaluationResults = await CoursewareItemProvider.getEvaluationResult(elementId || this.target.id);
    return evaluationResults;
  }

  async subscribeToResult(elementId, callback) {
    const subscription = { elementId, callback };
    this._subscriptions[elementId] = this._subscriptions[elementId] || [];
    this._subscriptions[elementId].push(subscription);
    this.getResult(elementId).then((feedbackResults) => {
      subscription.callback(feedbackResults || []);
    });
    return this._createUnsubscribe(elementId, subscription.callback);
  }

  _createUnsubscribe(elementId, callback) {
    return () => {
      this._subscriptions[elementId] = this._subscriptions[elementId].filter((subscription) => subscription.callback !== callback);
    };
  }

  async notifySubscribers(elementId) {
    const feedbackResults = await this.getResult(elementId);
    const subscriptions = this._subscriptions[elementId] || [];
    subscriptions.forEach((subscription) => subscription.callback(feedbackResults || []));
  }

  getInState(path, evaluationId) {
    if (evaluationId) {
      return CoursewareItemProvider.getInCompletedCoursewareState(this.target.id, evaluationId, path);
    }

    return CoursewareItemProvider.getInCoursewareState(this.target.id, path);
  }

  getInRawState(path = []) {
    return CoursewareItemProvider.getInCoursewareStateRaw(this.target.id, path);
  }

  setInState(path, value) {
    if (this.context === 'HISTORY') {
      return undefined;
    }

    return CoursewareItemProvider.setInCoursewareState(
      this.target.id,
      path,
      fromJS(value),
    );
  }

  subscribeToState(path, callbackFn, evaluationId, coursewareId) {
    if (evaluationId) {
      return CoursewareItemProvider.subscribeToCompletedCoursewareState(this.target.id, callbackFn, evaluationId, path, coursewareId);
    }

    return CoursewareItemProvider.subscribeToCoursewareState(
      this.target.id,
      callbackFn,
      path,
      coursewareId,
    );
  }

  subscribeToScope(...args) {
    return CoursewareItemProvider.subscribeToStudentScopeUpdate(...args);
  }

  getInProgress(path) {
    return CoursewareItemProvider.getInCoursewareProgress(this.target.id, path);
  }

  subscribeToProgress(path, callbackFn, coursewareId) {
    return CoursewareItemProvider.subscribeToCoursewareProgress(
      this.target.id,
      callbackFn,
      path,
      coursewareId,
    );
  }

  static build(args) {
    return new APICoursewareItemService(args);
  }
}

export default APICoursewareItemService;
